import { type ReactNode, useState, useCallback, useEffect, useMemo } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import classnames from 'clsx';

import { Checkbox } from 'ui-components/components/checkbox';
import { FlexRow } from 'ui-components/components/flex';
import { Gap } from 'ui-components/components/flex/gap';
import { CollapsibleList } from 'ui-components/components/collapsible-list';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { CollapsibleListSection } from 'ui-components/components/collapsible-list/collapsible-list-section';
import { CollapsibleListSectionHeader } from 'ui-components/components/collapsible-list/collapsible-list-section-header';
import { CollapsibleListSectionContent } from 'ui-components/components/collapsible-list/collapsible-list-section-content';

import { FormattedCurrency } from '../../components/formatted-currency';
import {
  INSURANCE_FIELD_TOGGLED_CSW,
  INSURANCE_AUTO_APPLY_CLICKED_CSW,
  INSURANCE_APPLY_FUTURE_DECISION_CLICKED_CSW,
} from '../../constants/analytics-events';
import { Segment } from '../../services/analytics-services/segment';
import { InsuranceProviders } from '../../enums/insurance-providers';
import { LocalStorageService } from '../../services/local-storage-service';

import { useInsurancePreference, INSURANCE_PREFERENCE } from '../../hooks/use-insurance-preference';
import { useUserHasConfigurationRole } from '../../hooks/use-user-role';
import { useFulfillmentPlannerContext } from '../context';
import messages from './messages';
import style from './insurance-promo.module.less';

const USER_HAS_VIEWED_INSURANCE_PROMO_KEY = 'user-has-viewed-insurance-promo';

type InsurancePromoProps = {
  children: ReactNode;
  className?: string;
  headerClassName?: string;
};

const getCollapsibleContentExplanation = (
  insurancePreference: string,
  insuranceProviderName: string | undefined,
  hasExplicitNonePreference: boolean,
): MessageDescriptor | undefined => {
  const isSkip = insurancePreference === INSURANCE_PREFERENCE.NONE && !hasExplicitNonePreference;
  const isNone = insuranceProviderName === InsuranceProviders.None.name && !isSkip;
  const isCarrier = insuranceProviderName === InsuranceProviders.Carrier.name;

  if (isSkip) {
    return messages.noSelectedPreference;
  }
  if (isNone) {
    return messages.noneProviderMessage;
  }
  if (isCarrier) {
    return messages.carrierProviderMessage;
  }
};

export const InsurancePromo = ({ children, className, headerClassName }: InsurancePromoProps) => {
  const {
    insuranceProvider,
    packages,
    parentComponentName,
    setShouldUpdateInsurancePreference,
    shouldUpdateInsurancePreference,
    isInsuranceOptionDirty,
  } = useFulfillmentPlannerContext();
  const { insurancePreference, hasExplicitNonePreference, insurancePreferenceForAnalytics } = useInsurancePreference();
  const isSkip = insurancePreference === INSURANCE_PREFERENCE.NONE && !hasExplicitNonePreference;
  const hasConfigurationRole = useUserHasConfigurationRole();
  const totalInsuredAmount = useMemo(
    () =>
      packages?.reduce((sum, p) => {
        return sum + (p?.insuredValue?.value ?? 0);
      }, 0) || 0,
    [packages],
  );

  const isParcelGuard = insuranceProvider?.name === InsuranceProviders.ParcelGuard.name;
  const isXCover = insuranceProvider?.name === InsuranceProviders.XCover.name;
  const isShipsurance = insuranceProvider?.name === InsuranceProviders.Shipsurance.name;

  const isOurCoverage = isParcelGuard || isXCover || isShipsurance;

  const shouldShowAutoInsureCheckbox =
    isOurCoverage &&
    hasConfigurationRole &&
    insurancePreference !== INSURANCE_PREFERENCE.INSURE_ALL &&
    // even though we consider shipsurance our coverage hide the checkbox because it is not walleted and no setting exists for it
    !isShipsurance &&
    !isSkip;

  const handleAutoInsureChange = useCallback(
    (checked: boolean) => {
      setShouldUpdateInsurancePreference(checked);
      Segment.trackEvent(INSURANCE_AUTO_APPLY_CLICKED_CSW, {
        checked,
        insuranceProvider: insuranceProvider?.name,
        insurancePreference: insurancePreferenceForAnalytics,
      });
    },
    [setShouldUpdateInsurancePreference, insuranceProvider?.name, insurancePreferenceForAnalytics],
  );

  const shouldShowApplyToFutureShipmentsCheckbox =
    hasConfigurationRole &&
    insuranceProvider &&
    isSkip &&
    !isShipsurance &&
    insuranceProvider?.insuranceProviderId !== InsuranceProviders.Carrier.insuranceProviderId &&
    insuranceProvider?.insuranceProviderId !== InsuranceProviders.External.insuranceProviderId &&
    (isInsuranceOptionDirty || insuranceProvider?.insuranceProviderId !== InsuranceProviders.None.insuranceProviderId);

  const handleApplyDecisionToFutureShipment = useCallback(
    (checked: boolean) => {
      setShouldUpdateInsurancePreference(checked);
      Segment.trackEvent(INSURANCE_APPLY_FUTURE_DECISION_CLICKED_CSW, {
        checked,
        insuranceProvider: insuranceProvider?.name,
        insurancePreference: insurancePreferenceForAnalytics,
      });
    },
    [setShouldUpdateInsurancePreference, insuranceProvider?.name, insurancePreferenceForAnalytics],
  );

  const [isOpen, setIsOpen] = useState(() => {
    if (isSkip) {
      return true;
    }
    if (isOurCoverage && insurancePreference === INSURANCE_PREFERENCE.INSURE_ALL) {
      return false;
    }
    if (!isOurCoverage) {
      const hasViewedPromo = LocalStorageService.getItem(USER_HAS_VIEWED_INSURANCE_PROMO_KEY);
      return !hasViewedPromo;
    }
    return true;
  });

  useEffect(() => {
    const hasViewedPromo = LocalStorageService.getItem(USER_HAS_VIEWED_INSURANCE_PROMO_KEY);
    if (!hasViewedPromo) {
      LocalStorageService.setItem(USER_HAS_VIEWED_INSURANCE_PROMO_KEY, true);
    }
  }, []);

  const toggleOpen = useCallback(() => {
    Segment.trackEvent(INSURANCE_FIELD_TOGGLED_CSW, {
      cswLocation: parentComponentName,
      isNowExpanded: !isOpen,
      insurancePreference: insurancePreferenceForAnalytics,
    });
    setIsOpen((prev) => !prev);
  }, [insurancePreferenceForAnalytics, parentComponentName, isOpen]);

  const contentExplanation = getCollapsibleContentExplanation(insurancePreference, insuranceProvider?.name, hasExplicitNonePreference);

  return (
    <Tooltip position='bottom' shouldHideTooltip={isOpen}>
      <CollapsibleList className={classnames(style['insurance-promo'], className)}>
        <CollapsibleListSection className={style['insurance-section']} open={isOpen}>
          <CollapsibleListSectionHeader
            className={isOurCoverage ? style['header-green'] : style['header-yellow']}
            contentClassName={style['header-content-wrapper']}
            onClick={toggleOpen}
            openCloseCaretVertical
            shouldPositionCaretRight
          >
            <FlexRow className={classnames(style['header-content'], headerClassName)} gap={Gap.Space8}>
              {isOurCoverage ? (
                <FontAwesomeIcon className={style['check-icon']} icon={faCheckCircle} />
              ) : (
                <FontAwesomeIcon className={style['warning-icon']} icon={faExclamationTriangle} />
              )}
              {isOurCoverage ? (
                <FormattedMessage {...messages.insuredByCoverage} />
              ) : (
                <FormattedMessage {...messages.notInsuredByCoverage} />
              )}
            </FlexRow>
          </CollapsibleListSectionHeader>
          <CollapsibleListSectionContent className={style['content-body']}>
            {contentExplanation && (
              <div className={style['promo-message']}>
                <FormattedMessage {...contentExplanation} />
              </div>
            )}
            {children}
            {shouldShowAutoInsureCheckbox && (
              <Checkbox
                checked={shouldUpdateInsurancePreference}
                className={style['auto-insure-checkbox']}
                onChange={handleAutoInsureChange}
              >
                <FormattedMessage {...messages.automaticallyInsure} />
              </Checkbox>
            )}
            {shouldShowApplyToFutureShipmentsCheckbox && (
              <Checkbox
                checked={shouldUpdateInsurancePreference}
                className={style['auto-insure-checkbox']}
                onChange={handleApplyDecisionToFutureShipment}
              >
                <FormattedMessage {...messages.applyDecisionCheckboxLabel} />
              </Checkbox>
            )}
          </CollapsibleListSectionContent>
        </CollapsibleListSection>
      </CollapsibleList>
      <TooltipContent>
        <div>
          <FormattedMessage
            {...messages.popoverInsurance}
            values={{ providerName: insuranceProvider?.name || InsuranceProviders.None.name }}
          />
        </div>
        {insuranceProvider && insuranceProvider.name !== InsuranceProviders.None.name ? (
          <div>
            <FormattedMessage {...messages.popoverInsureAmount} values={{ amount: <FormattedCurrency value={totalInsuredAmount} /> }} />
          </div>
        ) : null}
      </TooltipContent>
    </Tooltip>
  );
};
